"use client"
import { Link, useRouter } from '@/navigation'
import { Youtube, Facebook, Instagram, Linkedin, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { drAndCoachStaticData } from '@/constants/drAndCoachData'

const Footer = () => {
    const t = useTranslations('Footer')
    const router = useRouter()

    const footerLinks = {
        company: [
            { label: t('company.home'), href: '/' },
            { label: t('company.about'), href: '#whyus' },
            { label: t('company.services'), href: '#services' },
            { label: t('company.transform'), href: '/transforms' }
        ],
        help: [
            { label: t('help.services'), href: '#services' },
            { label: t('help.testimonials'), href: '#testimonials' },
            { label: t('help.faq'), href: '#common' }
        ],
        social: [
            { icon: Youtube, href: drAndCoachStaticData.youtube },
            { icon: Linkedin, href: drAndCoachStaticData.linkedIn },
            { icon: Facebook, href: drAndCoachStaticData.facebook },
            { icon: XIcon, href: drAndCoachStaticData.twitter },
            { icon: Instagram, href: drAndCoachStaticData.instagram }
        ]
    }

    const FooterSection = ({ title, children }: { title: string, children: React.ReactNode }) => (
        <div className='flex flex-col  gap-6 md:gap-8'>
            <h2 className='text-lg md:text-xl font-bold leading-tight text-white'>
                {title}
            </h2>
            <div className='w-full'>
                {children}
            </div>
        </div>
    )

    const FooterLink = ({ href, label, onClick }: { href?: string, label: string, onClick?: () => void }) => (
        onClick ? (
            <div onClick={onClick} className='footer-link hover:translate-x-2 transition-transform cursor-pointer'>
                {label}
            </div>
        ) : (
            <Link href={href || '/'} className='footer-link hover:translate-x-2 transition-transform'>
                {label}
            </Link>
        )
    )

    return (
        <footer className='bg-primary py-10 px-6 lg:px-40 text-white'>
            <div className='max-w-7xl mx-auto'>
                {/* Logo */}
                <div className="flex justify-center mb-16">
                    <Link href="/" className="w-32 md:w-40">
                        <img 
                            className="w-full" 
                            src="/logos/logo_white.webp" 
                            alt="Dr and Coach" 
                        />
                    </Link>
                </div>

                {/* Main Content Grid */}
                <div className='grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-12 md:mb-16'>
                    {/* Company Links */}
                    <FooterSection title={t('company.title')}>
                        <div className='flex flex-col gap-4'>
                            {footerLinks.company.map((link, index) => (
                                <FooterLink 
                                    key={index}
                                    href={link.href.startsWith('#') ? undefined : link.href}
                                    label={link.label}
                                    onClick={link.href.startsWith('#') ? () => router.push(link.href) : undefined}
                                />
                            ))}
                        </div>
                    </FooterSection>

                    {/* Help Links */}
                    <FooterSection title={t('help.title')}>
                        <div className='flex flex-col gap-4'>
                            {footerLinks.help.map((link, index) => (
                                <FooterLink 
                                    key={index}
                                    label={link.label}
                                    onClick={() => router.push(link.href)}
                                />
                            ))}
                        </div>
                    </FooterSection>

                    {/* Contact Info */}
                    <FooterSection title={t('contact.title')}>
                        <div className='flex flex-col gap-4'>
                            {[
                                drAndCoachStaticData.phone,
                                drAndCoachStaticData.email,
                                drAndCoachStaticData.address
                            ].map((item, index) => (
                                <p key={index} className='footer-link hover:scale-110 transition-transform text-sm md:text-base'>
                                    {item}
                                </p>
                            ))}
                        </div>
                    </FooterSection>

                    {/* Social Media */}
                    <FooterSection title={t('follow')}>
                        <div className='grid grid-cols-2 gap-y-4 gap-x-2'>
                            {footerLinks.social.map((social, index) => (
                                <a 
                                    key={index}
                                    href={social.href} 
                                    className='footer-link hover:scale-110 transition-transform flex items-center gap-1.5' 
                                    target='_blank'
                                    rel="noopener noreferrer"
                                >
                                    <social.icon className="w-5 h-5 md:w-6 md:h-6" />
                                    <span className="text-sm truncate">{social.icon.name}</span>
                                </a>
                            ))}
                        </div>
                    </FooterSection>
                </div>

                {/* Copyright */}
                <div className='border-t border-white/10 pt-6 md:pt-8'>
                    <p className='text-center text-white text-base font-normal md:text-lg'>
                        جميع الحقوق محفوظة © {new Date().getFullYear()} Dr & Coach
                        <br />
                        تم التطوير بواسطة <Link href="https://iconitech.co" className='text-xl hover:text-black transition-colors'>iconitech</Link>
                    </p>
                </div>
            </div>
        </footer>
    )
}

export default Footer